import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "@lib/firebase";
import {
  collection,
  addDoc,
  getDoc,
  doc,
  query,
  where,
  getDocs,
  limit,
} from "firebase/firestore";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "../common/NotificationContext";
import { serverTimestamp } from "firebase/firestore";
import { InputFieldVolunteer } from "../common/InputFieldVolunteer";

export const FormPendaftaranVolunteer = ({ labelColor = "text-slate-800" }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();

  const [formData, setFormData] = useState({
    namaDepan: "",
    namaBelakang: "",
    namaLengkap: "",
    telepon: "",
    nomorIdentitas: "",
    linkSertifikat: "",
    usernameInstagram: "",
    tanggalLahir: "",
    gender: "",
    negara: "Indonesia",
    provinsi: "",
    kota: "",
    alamat: "",
    motivasiDanPengalaman: "",
    ukuranJersey: "",
    posisiVolunteer: "",
  });

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [registrationStatus, setRegistrationStatus] = useState(null);

  const humanize = (str) => {
    if (!str) return "";
    if (str === "nomorIdentitas") return "Nomor Identitas";
    if (str === "motivasiDanPengalaman")
      return "Motivasi dan Pengalaman Volunteer";
    if (str === "ukuranJersey") return "Ukuran Jersey";
    if (str === "linkSertifikat") return "Link Sertifikat";
    if (str === "usernameInstagram") return "Username Instagram";
    const withSpaces = str.replace(/([A-Z])/g, " $1").trim();
    return withSpaces
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const checkRegistration = async () => {
    if (!user) return;

    try {
      const q = query(
        collection(db, "pendaftaranVolunteers"),
        where("uid", "==", user.uid),
        limit(1)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        setRegistrationStatus("registered");
      } else {
        setRegistrationStatus("not_registered");
      }
    } catch (err) {
      setErrorMsg("Gagal memeriksa status pendaftaran. Silakan coba lagi.");
      showNotification("error", "Gagal memeriksa status pendaftaran.");
    }
  };

  useEffect(() => {
    if (!user) {
      navigate("/Login");
      showNotification(
        "error",
        "Silakan login untuk mendaftar sebagai volunteer."
      );
      return;
    }

    const initialize = async () => {
      try {
        const docRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(docRef);
        let userData = {};

        if (docSnap.exists()) {
          userData = docSnap.data();
          let namaDepan = userData.namaDepan || "";
          let namaBelakang = userData.namaBelakang || "";
          let namaLengkap = userData.namaLengkap || "";

          if (!namaDepan && !namaBelakang && namaLengkap) {
            const namaSplit = namaLengkap.trim().split(/\s+/);
            namaDepan = namaSplit[0] || "";
            namaBelakang = namaSplit.slice(1).join(" ") || "";
          }

          setFormData((prev) => ({
            ...prev,
            namaDepan,
            namaBelakang,
            namaLengkap: namaLengkap || `${namaDepan} ${namaBelakang}`.trim(),
            telepon: userData.telepon || "",
            nomorIdentitas: userData.nomorIdentitas || "",
            linkSertifikat: userData.linkSertifikat || "",
            usernameInstagram: userData.usernameInstagram || "",
            tanggalLahir: userData.tanggalLahir || "",
            gender: userData.gender || "",
            negara: userData.negara || "Indonesia",
            provinsi: userData.provinsi || "",
            kota: userData.kota || "",
            alamat: userData.alamat || "",
            motivasiDanPengalaman: userData.motivasiDanPengalaman || "",
            ukuranJersey: userData.ukuranJersey || "",
          }));
        } else {
          const namaSplit = (user.displayName || "").trim().split(/\s+/);
          const namaDepan = namaSplit[0] || "";
          const namaBelakang = namaSplit.slice(1).join(" ") || "";
          setFormData((prev) => ({
            ...prev,
            namaDepan,
            namaBelakang,
            namaLengkap:
              namaDepan && namaBelakang
                ? `${namaDepan} ${namaBelakang}`.trim()
                : "",
            linkSertifikat: "",
            usernameInstagram: "",
          }));
        }

        await checkRegistration();
      } catch (err) {
        setErrorMsg("Gagal mengambil data pengguna.");
        showNotification("error", "Gagal mengambil data pengguna.");
      }
    };

    initialize();
  }, [navigate, showNotification, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "namaDepan" || name === "namaBelakang") {
      setFormData((prev) => {
        const updated = { ...prev, [name]: value };
        updated.namaLengkap = `${updated.namaDepan || ""} ${
          updated.namaBelakang || ""
        }`.trim();
        return updated;
      });
    } else if (name === "namaLengkap") {
      const namaSplit = value.trim().split(/\s+/);
      setFormData((prev) => ({
        ...prev,
        namaLengkap: value,
        namaDepan: namaSplit[0] || "",
        namaBelakang: namaSplit.slice(1).join(" ") || "",
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const validateFormat = (field, value, negara = "Indonesia") => {
    const label = humanize(field);

    if (value === undefined || value === null || value === "") {
      if (field === "linkSertifikat" || field === "usernameInstagram") {
        return "";
      }
      return `${label} wajib diisi`;
    }
    if (
      (field === "namaDepan" || field === "namaBelakang") &&
      !/^[A-Za-z\s]{1,50}$/.test(value)
    ) {
      return `${label} hanya boleh huruf dan spasi, maksimum 50 karakter.`;
    }
    if (field === "telepon" && !/^\+?\d{8,15}$/.test(value)) {
      return `${label} harus 8-15 digit, boleh diawali dengan "+".`;
    }
    if (field === "nomorIdentitas") {
      if (negara === "Indonesia") {
        if (!/^\d{16}$/.test(value)) {
          return `${label} (KTP) harus 16 digit untuk peserta dari Indonesia.`;
        }
      } else {
        if (!/^[A-Za-z0-9]{5,20}$/.test(value)) {
          return `${label} (Paspor/Identitas) harus 5-20 karakter alfanumerik untuk peserta asing.`;
        }
      }
    }
    if (
      field === "linkSertifikat" &&
      value &&
      !/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(value)
    ) {
      return `${label} harus berupa URL yang valid, misalnya: "https://linksertifikatanda"`;
    }
    if (
      field === "usernameInstagram" &&
      value &&
      !/^[A-Za-z0-9._]{1,30}$/.test(value)
    ) {
      return `${label} hanya boleh huruf, angka, titik, atau garis bawah, maksimum 30 karakter, misalnya: "user_name"`;
    }
    if (field === "tanggalLahir") {
      const date = new Date(value);
      const eventDate = new Date("2025-09-27");
      const minAgeDate = new Date(
        eventDate.getFullYear() - 18,
        eventDate.getMonth(),
        eventDate.getDate()
      );
      if (isNaN(date.getTime()) || date >= eventDate) {
        return `${label} harus di masa lalu sebelum 27/9/2025, misalnya: "2005-09-27"`;
      } else if (date > minAgeDate) {
        return `Volunteer harus berusia minimal 18 tahun pada 27/9/2025, misalnya: "2007-09-27" atau sebelumnya`;
      }
    }
    if (field === "gender" && !["Laki-laki", "Perempuan"].includes(value)) {
      return `${label} harus Laki-laki atau Perempuan.`;
    }
    if (field === "alamat" && (value.trim().length < 5 || value.trim().length > 200)) {
      return `${label} harus antara 5 hingga 200 karakter.`;
    }
    if (
      field === "motivasiDanPengalaman" &&
      !/^[A-Za-z0-9\s,.!?-]{10,500}$/.test(value)
    ) {
      return `${label} harus 10-500 karakter, boleh huruf, angka, spasi, dan tanda baca tertentu.`;
    }
    if (
      field === "ukuranJersey" &&
      !["XS", "S", "M", "L", "XL", "XXL"].includes(value)
    ) {
      return `${label} harus dipilih dari daftar ukuran yang tersedia.`;
    }
    if (
      field === "posisiVolunteer" &&
      ![
        "Marshalls",
        "Water Station Crew",
        "Racepack Collection Crew",
        "Race Venue Crew",
        "Medis & Rescue",
        "Photographer",
        "Check Point",
        "Logistik",
      ].includes(value)
    ) {
      return `${label} harus dipilih dari daftar posisi yang tersedia.`;
    }
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");

    const requiredFields = [
      "namaDepan",
      "namaBelakang",
      "telepon",
      "nomorIdentitas",
      "tanggalLahir",
      "gender",
      "negara",
      "provinsi",
      "kota",
      "alamat",
      "motivasiDanPengalaman",
      "ukuranJersey",
      "posisiVolunteer",
    ];

    const errors = {};
    for (const field of requiredFields) {
      const error = validateFormat(field, formData[field], formData.negara);
      if (error) {
        errors[field] = error;
      }
    }

    if (formData.linkSertifikat) {
      const error = validateFormat("linkSertifikat", formData.linkSertifikat);
      if (error) errors.linkSertifikat = error;
    }
    if (formData.usernameInstagram) {
      const error = validateFormat(
        "usernameInstagram",
        formData.usernameInstagram
      );
      if (error) errors.usernameInstagram = error;
    }

    if (Object.keys(errors).length > 0) {
      const firstError = Object.values(errors)[0];
      setErrorMsg(firstError);
      showNotification("error", firstError);
      setLoading(false);
      return;
    }

    try {
      const sanitizedFormData = {
        namaDepan: formData.namaDepan?.trim().replace(/\s+/g, " "),
        namaBelakang: formData.namaBelakang?.trim().replace(/\s+/g, " "),
        namaLengkap: formData.namaLengkap?.trim().replace(/\s+/g, " "),
        telepon: formData.telepon?.trim().replace(/\s+/g, ""),
        nomorIdentitas: formData.nomorIdentitas?.trim().replace(/\s+/g, ""),
        linkSertifikat: formData.linkSertifikat?.trim() || "",
        usernameInstagram: formData.usernameInstagram?.trim() || "",
        tanggalLahir: formData.tanggalLahir?.trim(),
        gender: formData.gender?.trim(),
        negara: formData.negara?.trim(),
        provinsi: formData.provinsi?.trim(),
        kota: formData.kota?.trim(),
        alamat: formData.alamat?.trim().replace(/\s+/g, " "),
        motivasiDanPengalaman: formData.motivasiDanPengalaman
          ?.trim()
          .replace(/\s+/g, " "),
        ukuranJersey: formData.ukuranJersey?.trim(),
        posisiVolunteer: formData.posisiVolunteer?.trim(),
      };

      const payload = {
        ...sanitizedFormData,
        uid: user.uid,
        createdAt: serverTimestamp(),
        status: null,
      };

      await addDoc(collection(db, "pendaftaranVolunteers"), payload);
      setSuccessMsg("Pendaftaran volunteer berhasil! Menunggu review panitia.");
      setRegistrationStatus("registered");
      showNotification(
        "success",
        "Pendaftaran volunteer berhasil! Menunggu review panitia."
      );
      setTimeout(() => navigate("/MyRegistration"), 2000);
    } catch (error) {
      const errorMessage =
        error.message || "Pendaftaran volunteer gagal. Silakan coba lagi.";
      setErrorMsg(errorMessage);
      showNotification("error", errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white shadow-xl px-4 sm:px-6 lg:px-8 z-0">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-4 py-20 overflow-visible">
        <div className="w-full md:w-3/4 max-w-4xl bg-white p-4 rounded-xl border border-slate-200 backdrop-blur-md">
          <h2 className="text-2xl font-semibold text-left mb-4">
            Form Pendaftaran Volunteer JOTR 2025
          </h2>
          <form id="form-pendaftaran-volunteer" onSubmit={handleSubmit}>
            <InputFieldVolunteer
              formData={formData}
              handleChange={handleChange}
              isFormDisabled={registrationStatus === "registered" || loading}
              labelColor={labelColor}
            />
          </form>
        </div>

        <div className="w-full md:w-1/4 max-w-sm bg-white p-4 rounded-xl border border-slate-200 backdrop-blur-md flex flex-col gap-4">
          <h2 className="text-2xl font-semibold text-left">
            Informasi Pendaftaran
          </h2>
          <div className="text-lg text-left">
            <p>
              Mohon lengkapi data Anda. Panitia akan menghubungi apabila lolos
              seleksi. Terima kasih atas partisipasinya!
            </p>
          </div>

          {(errorMsg || successMsg) && (
            <p
              className={`text-left font-medium ${
                errorMsg ? "text-rose-600" : "text-slate-800"
              }`}
            >
              {errorMsg || successMsg}
            </p>
          )}

          <div>
            {registrationStatus === "registered" ? (
              <button
                type="button"
                className="w-full py-3 bg-slate-400 font-semibold rounded-xl cursor-not-allowed opacity-50"
                disabled
              >
                Sudah Terdaftar
              </button>
            ) : (
              <button
                type="submit"
                form="form-pendaftaran-volunteer"
                className={`w-full py-3 bg-lime-600 hover:bg-lime-700 transition-all duration-300 text-white font-semibold rounded-xl ${
                  loading ? "cursor-not-allowed opacity-50" : ""
                }`}
                disabled={loading}
              >
                {loading ? "Loading..." : "Daftar Volunteer"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
